type RequestMessageDataType = {
    type: "subscribe" | "refresh" | "unsubscribe";
    subscriptionJson: string;
};

type ResponseMessageDataType =
    | { type: "subscribed"; subscriptionJson: string }
    | { type: "unsubscribed" };

const getServiceWorkerAsync = async (): Promise<ServiceWorkerRegistration> =>
    (await navigator.serviceWorker.getRegistration("/sw.notify.js")) ??
    (await navigator.serviceWorker.register("/sw.notify.js"));

const createSubscriptionAsync = async (
    registration: ServiceWorkerRegistration,
): Promise<PushSubscription> =>
    await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey:
            "BLtILXLNN66LVjqnq_Yd_eHS55f2ik8K2UTIKMvmBCpf5fYJVe9XpdncxIexNaZ0-qxiKRTaLFjLhfAT5kPLRVA",
    });

export const useNotifySubscriberStore = defineStore("notifySubscriber", () => {
    const subscription = ref<PushSubscription | null>(null);
    const workerRegistration = ref<ServiceWorkerRegistration | null>(null);
    const listenersAbortController = ref(new AbortController());

    const onWorkerMessageRecevied = (event: MessageEvent<any>) => {
        const data = event.data as ResponseMessageDataType;
        console.debug("[Notify] Received message from sw", data.type);
        if (data.type === "subscribed") {
            console.log("[Notify] Subscription added");
            subscription.value = JSON.parse(data.subscriptionJson);
        } else if (data.type === "unsubscribed") {
            console.log("[Notify] Subscription removed");
            subscription.value = null;
        }
    };

    const initializeAsync = async () => {
        try {
            if (!!!workerRegistration.value) {
                listenersAbortController.value = new AbortController();

                console.debug(`[Notify] Getting sw....`);
                workerRegistration.value = await getServiceWorkerAsync();
                if (!!!workerRegistration.value) {
                    console.error("[Notify] Sw registration failed");
                    return false;
                }

                console.debug("[Notify] Setting up sw listener...");
                navigator.serviceWorker.addEventListener(
                    "message",
                    onWorkerMessageRecevied,
                    { signal: listenersAbortController.value.signal },
                );
            }

            console.debug("[Notify] Retrieving existing subscription...");
            subscription.value =
                await workerRegistration.value.pushManager.getSubscription();

            console.log("[Notify] Initialized");
            return true;
        } catch (error) {
            console.error(error);
            workerRegistration.value = null;
            listenersAbortController.value.abort();
            listenersAbortController.value = new AbortController();
            return false;
        }
    };

    const subscribeAsync = async () => {
        try {
            if (!!subscription.value) {
                console.debug("[Notify] Already subscribed");
                return;
            }
            if (!(await initializeAsync())) {
                subscription.value = null;
                return;
            }
            if (!(await ensureNotificationPermissionAsync())) {
                console.warn("[Notify] Missing permissions");
                subscription.value = null;
                return;
            }
            if (!!!workerRegistration.value) {
                console.error("[Notify] Sw registration missing");
                return;
            }

            console.log("[Notify] Subscribing...");
            subscription.value = await createSubscriptionAsync(
                workerRegistration.value,
            );
            await postMessageAsync({
                type: "subscribe",
                subscriptionJson: JSON.stringify(subscription.value),
            });
        } catch (error) {
            subscription.value = null;
            console.error(error);
        }
    };

    const unsubscribeAsync = async () => {
        try {
            if (!(await initializeAsync()) || !!!subscription.value) {
                return;
            }

            console.log("[Notify] Unsubscribing...");
            await subscription.value.unsubscribe();
            await postMessageAsync({
                type: "unsubscribe",
                subscriptionJson: JSON.stringify(subscription.value),
            });
        } catch (error) {
            console.error(error);
        }
    };

    const refreshAsync = async () => {
        try {
            if (!(await initializeAsync())) {
                subscription.value = null;
                return;
            }
            if (!!!subscription.value) {
                console.log("[Notify] Not subscribed");
                return;
            }
            if (!(await ensureNotificationPermissionAsync())) {
                console.warn("[Notify] Missing permissions");
                subscription.value = null;
                return;
            }
            if (!!!workerRegistration.value) {
                console.error("[Notify] Sw registration missing");
                return;
            }

            console.log("[Notify] Refreshing subscription...");
            await postMessageAsync({
                type: "refresh",
                subscriptionJson: JSON.stringify(subscription.value),
            });
        } catch (error) {
            subscription.value = null;
            console.error(error);
        }
    };

    const postMessageAsync = async (message: RequestMessageDataType) => {
        const swController = (await navigator.serviceWorker.ready).active;
        if (!!swController) {
            console.debug(`[Notify] Sending ${message.type} to ServiceWorker`);
            swController.postMessage(message);
        } else {
            console.error("[Notify] ServiceWorker controller is NULL!");
        }
    };

    return {
        isSubscribed: computed(() => !!subscription.value),

        subscribeAsync,
        refreshAsync,
        unsubscribeAsync,
    };
});

const ensureNotificationPermissionAsync = async () => {
    const result = await Notification.requestPermission();
    return result == "granted";
};
